import React, { useState, useEffect } from 'react';
import {
    Button,
    Col,
    ConfigProvider,
    Form,
    Input,
    Modal,
    Row,
    Upload,
    Switch,
    Slider,
    Tooltip
} from 'antd';
import { BiCheck, BiX } from "react-icons/bi";
import { UploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import locale from "antd/es/locale/es_ES";
import HomeColors from "../../../../styles/HomeColors";
import { PostAula } from "../../../../dao/PostAula";
import { PostAulaIdProjecte } from "../../../../dao/PostAulaIdProjecte";
import { useParams } from "react-router-dom";

interface Props {
    element: any;
    visible: boolean;
    onClose: (updated: boolean, data?: any) => void;
}

const ProjecteModal: React.FC<Props> = ({ element, visible, onClose }) => {
    const [form] = Form.useForm();
    const [preview, setPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [iaEnabled, setIaEnabled] = useState<boolean>(false);
    const { id_aula } = useParams();

    const normalizeImg = (img?: string | null) => {
        if (!img) return null;
        if (img.startsWith("base64:")) {
            const parts = img.split(":");
            img = parts[parts.length - 1];
        }
        if (img.startsWith("/9j/")) return `data:image/jpeg;base64,${img}`;
        return `data:image/png;base64,${img}`;
    };

    useEffect(() => {
        if (element) {
            form.setFieldsValue(element);
            setPreview(normalizeImg(element.img));
            setIaEnabled(element.ia || false);
        } else {
            form.resetFields();
            setPreview(null);
            setIaEnabled(false);
        }
    }, [element, form]);

    const handleCancel = () => {
        onClose(false);
        setPreview(null);
    };

    async function handleSubmit() {
        setLoading(true);
        try {
            const values = await form.validateFields();
            const imagenBase64 = preview ? preview.split(',')[1] : null;
            const payload = { ...values, img: imagenBase64 };
            await PostAulaIdProjecte(id_aula + "", payload);
            onClose(true, payload);
        } catch (e) {
            console.error("Error saving project:", e);
        } finally {
            setLoading(false);
        }
    }

    return (
        <ConfigProvider locale={locale}>
            <Modal
                title={<div style={{ fontSize: '1.2rem', color: HomeColors.colors.color4 }}>Crear / Editar Sessió</div>}
                open={visible}
                onCancel={handleCancel}
                footer={null}
                width={800}
                centered
                maskClosable={false}
            >
                <Form form={form} onFinish={handleSubmit} layout="vertical" requiredMark="optional">

                    {/* --- Basic Info Section --- */}
                    <div style={{ padding: '10px 0' }}>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item label="ID" name="id">
                                    <Input disabled style={{ fontWeight: 'bold', textAlign: 'center' }} />
                                </Form.Item>
                            </Col>
                            <Col span={18}>
                                <Form.Item label="Nom del Projecte" name="nom" rules={[{ required: true, message: 'El nom és obligatori' }]}>
                                    <Input placeholder="Ex: Projecte Final: Desenvolupament Web" size="large" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item label="Context (Prompt inicial per a la IA)" name="context" rules={[{ required: true, message: 'El context és obligatori' }]}>
                            <Input.TextArea rows={3} placeholder="Descriu el context d'aquest projecte per a la IA." style={{ resize: 'none' }} />
                        </Form.Item>
                    </div>

                    {/* --- Image Upload --- */}
                    <Form.Item label="Imatge de Portada" name="imagen">
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                            <Upload
                                beforeUpload={() => false}
                                maxCount={1}
                                showUploadList={false}
                                onChange={(info) => {
                                    if (info.fileList.length > 0) {
                                        const file = info.fileList[0].originFileObj as File;
                                        const reader = new FileReader();
                                        reader.onloadend = () => setPreview(reader.result as string);
                                        reader.readAsDataURL(file);
                                    } else setPreview(null);
                                }}
                            >
                                <Button icon={<UploadOutlined />}>Seleccionar Imatge</Button>
                            </Upload>

                            {preview && (
                                <div style={{ border: '1px solid #ddd', padding: '5px', borderRadius: '4px', background: 'white' }}>
                                    <img src={preview} alt="preview" style={{ maxHeight: '80px', display: 'block' }} />
                                </div>
                            )}
                        </div>
                    </Form.Item>



                    {/* --- Footer Buttons --- */}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "30px", borderTop: '1px solid #eee', paddingTop: '15px' }}>
                        <Button onClick={handleCancel} size="large">
                            Cancel·lar
                        </Button>
                        <Button
                            loading={loading}
                            htmlType="submit"
                            type="primary"
                            size="large"
                            icon={<BiCheck />}
                            style={{ backgroundColor: HomeColors.colors.color4 }}
                        >
                            Guardar Canvis
                        </Button>
                    </div>
                </Form>
            </Modal>
        </ConfigProvider>
    );
};

export default ProjecteModal;
